import { faCartPlus, faDeleteLeft, faBars, faLink, faLocation, faTrash, faX } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { memo, useEffect, useState, useRef, Fragment } from 'react';
import { toast } from 'react-toastify';
import Tooltip from '@mui/material/Tooltip';
import moment from 'moment';
import { FeatureLike } from 'ol/Feature';
import { FeatureInfoStore, FeatureInfoStoreData, FeatureWindowPresentation, GetFeatureInfoData } from '../Map.types';
import { MapPresenter } from '../MapPresenter';
import FloatingWindow from './FloatingWindow';
import { SuperDangerouslySetHtmlContent, SafelySetHtmlContent } from "./DangerousHTML";
import { isTrustedFeatureUrl } from "./DangerousHTML.utils";
import { FloatingWindowDimensions, LoginState, OutletIProps } from "../../../components/sitebase/Base.types";
import LoadingSpinner from "../../../components/loadingSpinner/LoadingSpinner";
import { useOutletContext } from "react-router-dom";
import FeaturePointErrorReport from "./FeaturePointErrorReport";

import 'react-toastify/dist/ReactToastify.css';
import '../css/FeatureInfo.css';
import { UseFeatureInfoReturn } from "../hooks/useFeatureInfo";
import Dropdown from "../../../components/common/Dropdown";
import IS_EFAS from "../../../components/sitebase/Environment";

export { FeatureWindowPresentation } from '../Map.types';

function FeatureContent(props: { index: number, feature: GetFeatureInfoData }) {
    const [template, setTemplate] = useState<string>("");
    const context = useOutletContext<OutletIProps>();

    useEffect(() => {
        if (props.feature.url === "vector") {
            let featureProperties;
            try {
                featureProperties = (props.feature.data as FeatureLike[])[props.index].getProperties();
            } catch {
                featureProperties = (props.feature.data as FeatureLike[])[0].getProperties();
            }
            if ("geometry" in featureProperties) { delete featureProperties["geometry"] };
            // join all properties into a URL GET string with &-separation and key=value pairs
            const queryString = Object.entries(featureProperties).map(([key, value]) => `${encodeURIComponent(key)}=${encodeURIComponent(value as string)}`).join('&');
            const queryName = props.feature.layerName.replace('mapserver:', '');
            fetch(`/api/fms/template/efas/${props.feature.forecastDate}/${queryName}?token=${context.user.token}&` + queryString).then(res => res.text()).then(text => setTemplate(text));
        }
    }, [props.feature, props.index, context.user.token]);

    if (props.feature.url === "vector") {
        return !template && <LoadingSpinner /> || <SuperDangerouslySetHtmlContent html={template} />
        // implement & fetch template from fms then string replace and render
    } else if (isTrustedFeatureUrl(props.feature.url)) {
        return <SuperDangerouslySetHtmlContent html={props.feature.data as string} />
    } else {
        // External WMS from an untrusted host: sanitise before rendering.
        return <SafelySetHtmlContent html={props.feature.data as string} />
    }
}
function FeatureAccordionTabContent(props: { id: number, fisd: FeatureInfoStoreData, onAddToCart: (feature: GetFeatureInfoData) => void, latestForecast: string }) {
    const context = useOutletContext<OutletIProps>();
    return <div className="tab-content py-2 px-1" id={`featureinfo-tabContent-${props.id}`}>
        {props.fisd.features.map((feature, index) => {
            return <Fragment key={index}><div
                className={`fs-8 tab-pane fade` + (index === 0 ? ' show active' : '')}
                id={`featureinfo-tab-pane-${props.id}-${index}`}
                role="tabpanel"
                aria-labelledby={`featureinfo-tab-${props.id}-${index}`}
                tabIndex={index}>
                {context.user.forecaster && (feature.name.includes("Reporting Points") || feature.name.includes("ERIC Notification Points")) && Boolean(feature.notificationpoint) &&
                    <div className="d-flex flex-row justify-content-end fs-9">
                        <div className="d-flex flex-column">
                            <button className="btn btn-warning border-black fs-9" type="button" onClick={(_) => props.onAddToCart(feature)}>Add to cart <FontAwesomeIcon icon={faCartPlus} /></button>
                            {props.latestForecast && feature.forecastDate && moment.utc(feature.forecastDate).isBefore(moment.utc(props.latestForecast)) &&
                                <span className="text-danger ms-2 my-auto" style={{ fontSize: "0.75rem" }}>This point is not from the latest forecast</span>
                            }
                        </div>
                    </div>}
                <FeatureContent index={index} feature={feature} />
            </div>
                {/* special case for the pp_tab to be rendered separately */}
                {Boolean(feature.pp_tab) && <div
                    className={`fs-8 tab-pane fade`}
                    id={`featureinfo-tab-pane-${props.id}-${index}-pp`}
                    role="tabpanel"
                    aria-labelledby={`featureinfo-tab-${props.id}-${index}-pp`}
                    tabIndex={0}>
                    <FeatureContent index={index} feature={{ ...feature, data: feature.pp_tab }} />
                </div>
                }
            </Fragment>
        })}
    </div>
};
function FeatureAccordionTabList(props: { id: number, fisd: FeatureInfoStoreData }) {
    return <ul className="nav nav-tabs" role="tablist">
        {props.fisd.features.map((feature, index) => {
            return <Fragment key={index}><li className="nav-item fs-8" role="presentation">
                <button className={`nav-link` + (index === 0 ? ' active' : '')}
                    id={`featureinfo-tab-${props.id}-${index}`}
                    data-bs-toggle="tab"
                    data-bs-target={`#featureinfo-tab-pane-${props.id}-${index}`}
                    type="button"
                    role="tab"
                    aria-controls={`#featureinfo-tab-pane-${props.id}-${index}`}
                    aria-selected={(index === 0 ? true : false)}>
                    <span style={{ backgroundColor: "#a8829f", borderRadius: "25px" }} className="me-1">&nbsp;&nbsp;&nbsp;&nbsp;</span>
                    <span>{feature.name}</span>
                    {/* <div className='fs-9 fw-lighter fst-italic'>{feature.source}</div> */}
                </button>
            </li>
                {/* special case for the pp_tab to be rendered separately */}
                {Boolean(feature.pp_tab) && <li className="nav-item fs-8" role="presentation">
                    <button className={`nav-link`}
                        id={`featureinfo-tab-${props.id}-${index}-pp`}
                        data-bs-toggle="tab"
                        data-bs-target={`#featureinfo-tab-pane-${props.id}-${index}-pp`}
                        type="button"
                        role="tab"
                        aria-controls={`#featureinfo-tab-pane-${props.id}-${index}-pp`}>
                        <span style={{ backgroundColor: "#a8829f", borderRadius: "25px" }} className="me-1">&nbsp;&nbsp;&nbsp;&nbsp;</span>
                        <span>Post-processing</span>
                        {/* <div className='fs-9 fw-lighter fst-italic'>{feature.source}</div> */}
                    </button>
                </li>
                }
            </Fragment>
        })}
    </ul>
}

const FeatureItemActions = (props: {
    presenter: MapPresenter,
    fisd: FeatureInfoStoreData,
    draggableWindow: React.RefObject<HTMLDivElement | null>,
    anonymousUser: boolean,
    context: OutletIProps,
    inlineCenter?: boolean,
}) => {
    return (
        <div className="d-flex flex-row">
            {props.inlineCenter && (
                <Tooltip title="Center map on point" placement="bottom" arrow disableInteractive>
                    <div className="btn me-1 my-auto" role="button" onClick={(_) => { props.presenter.zoomToFeature(props.fisd, props.draggableWindow) }}>
                        <FontAwesomeIcon icon={faLocation} />
                    </div>
                </Tooltip>
            )}

            {!props.anonymousUser && IS_EFAS &&
                <div className="py-2">
                    <FeaturePointErrorReport fisd={props.fisd} context={props.context} />
                </div>
            }
            <Dropdown
                toggleButton={
                    <div className="btn me-1 my-auto" role="button" data-bs-toggle="dropdown" aria-expanded="false">
                        <FontAwesomeIcon icon={faBars} />
                    </div>}

                keepMounted={false}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'left',
                }}
                transformOrigin={{
                    vertical: 'top',
                    horizontal: 'left',
                }}
                width='20rem'
            >
                <div className="d-flex flex-column">
                    {!props.inlineCenter && (
                        <div role="button" className="btn text-start" onClick={(_) => { props.presenter.zoomToFeature(props.fisd, props.draggableWindow) }}>
                            <FontAwesomeIcon icon={faLocation} /> Center map on point
                        </div>
                    )}
                    <Dropdown
                        toggleButton={
                            <div role="button" className="btn dropdown-toggle">
                                <FontAwesomeIcon icon={faLink} /> Copy point information URL
                            </div>
                        }
                        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
                        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                        width='25rem'
                        closeOnItemClick={true}
                    >
                        {props.fisd.features.map((feature, index) => (
                            <div
                                key={index}
                                role="button"
                                className="btn text-start w-100"
                                onClick={() => {
                                    navigator.clipboard.writeText(feature.url.replace("html%2Fpoint", "text%2Fhtml"));
                                    toast.success(`Copied URL for ${feature.name}`);
                                }}
                            >
                                {feature.name}
                            </div>
                        ))}
                    </Dropdown>
                </div>
            </Dropdown>
        </div>
    )
}

function FeatureAccordionButtons(props: {
    id: number,
    fisd: FeatureInfoStoreData,
    presenter: MapPresenter,
    presentation?: FeatureWindowPresentation,
    collapsed: boolean,
    setCollapsed: (collapsed: boolean) => void,
    draggableWindow: React.RefObject<HTMLDivElement | null>,
    setFeatureList: (featureList: FeatureInfoStore) => void,
    anonymousUser: boolean,
    context: OutletIProps
}) {

    return <>
        <button
            className={`fi-collapse-button accordion-button ` + (props.collapsed ? "" : "collapsed")}
            type="button"
            // data-bs-toggle="collapse"
            // data-bs-target={`#collapse${props.id}`}
            aria-expanded={props.id === 0 ? 'true' : 'false'}
            aria-controls={`collapse${props.id}`}
            onClick={() => props.setCollapsed(!props.collapsed)}
        >
            {props.fisd.name}
        </button>
        <FeatureItemActions
            presenter={props.presenter}
            fisd={props.fisd}
            draggableWindow={props.draggableWindow}
            anonymousUser={props.anonymousUser}
            context={props.context}
        />
        <Tooltip title="Remove point" arrow disableInteractive>
            <button className="btn" type="button" onClick={(_) => { props.setFeatureList(props.presenter.removeFeatureInfo(props.id)); }}><FontAwesomeIcon icon={faTrash} /></button>
        </Tooltip>
    </>
}
const FeatureAccordionItem = memo(function (props: {
    id: number,
    fisd: FeatureInfoStoreData,
    presenter: MapPresenter,
    presentation?: FeatureWindowPresentation,
    collapsed: boolean,
    draggableWindow: React.RefObject<HTMLDivElement | null>,
    setFeatureList: (featureList: FeatureInfoStore) => void,
    onSetCartCounter: (count: number) => void
    anonymousUser: boolean,
    context: OutletIProps,
    latestForecast: string
}) {

    // collapsed means OPEN, it should always be true for non-list presentations
    // const collapsed = props.feature.presentation === FeatureWindowPresentation.LIST && props.collapsed || true;
    const [collapsed, setCollapsed] = useState<boolean>(props.presentation === FeatureWindowPresentation.LIST ? props.collapsed : true);

    useEffect(() => {
        setCollapsed(props.presentation === FeatureWindowPresentation.LIST ? props.collapsed : true);
    }, [props.collapsed, props.presentation]);

    return <div className="accordion-item">
        <div className='accordion-header' id={`heading${props.id}`}>
            <div className='d-flex'>
                <FeatureAccordionButtons
                    id={props.id}
                    fisd={props.fisd}
                    presenter={props.presenter}
                    presentation={props.presentation}
                    collapsed={collapsed}
                    setCollapsed={setCollapsed}
                    draggableWindow={props.draggableWindow}
                    setFeatureList={props.setFeatureList}
                    anonymousUser={props.anonymousUser}
                    context={props.context}
                />
            </div>
        </div>
        <div id={`collapse${props.id}`} className={`accordion-collapse collapse ` + (collapsed ? "show" : "")} aria-labelledby={`heading${props.id}`} data-bs-parent="#featureListAccordion">
            <div id={`featureid-${props.id}`} className="accordion-body efas-feature-info">
                {collapsed && <>
                    <FeatureAccordionTabList id={props.id} fisd={props.fisd} />
                    <FeatureAccordionTabContent id={props.id} fisd={props.fisd} latestForecast={props.latestForecast} onAddToCart={(feature) => {
                        props.presenter.addToCart(feature).then(
                            (res) => {
                                switch (res.status) {
                                    case 200:
                                        toast.success("Added to cart");
                                        break;
                                    case 406:
                                        toast.warning("Point is already in an open notification");
                                        break;
                                    case 409:
                                        toast.warning("Already in cart");
                                        break;
                                    default:
                                        toast.error("Failed to add to cart");
                                        break;
                                };
                                return res.json();
                            }
                        ).then((res) => {
                            if (res.cartlength) props.onSetCartCounter(res.cartlength);
                        });
                    }} />
                </>}
            </div>
        </div>
    </div>

});

function FeatureSingleItem(props: {
    id: number,
    fisd: FeatureInfoStoreData,
    presenter: MapPresenter,
    presentation?: FeatureWindowPresentation,
    collapsed: boolean,
    draggableWindow: React.RefObject<HTMLDivElement | null>,
    setFeatureList: (featureList: FeatureInfoStore) => void,
    onSetCartCounter: (count: number) => void
    anonymousUser: boolean,
    context: OutletIProps,
    latestForecast: string
}) {

    return <div id={`featureid-${props.id}`} className="efas-feature-info">
        <div className="d-flex flex-row">
            <FeatureAccordionTabList id={props.id} fisd={props.fisd} />
            <div className="ms-auto">
                <FeatureItemActions
                    presenter={props.presenter}
                    fisd={props.fisd}
                    draggableWindow={props.draggableWindow}
                    anonymousUser={props.anonymousUser}
                    context={props.context}
                    inlineCenter={true}
                />
            </div>
        </div>
        <FeatureAccordionTabContent id={props.id} fisd={props.fisd} latestForecast={props.latestForecast} onAddToCart={(feature) => {
            props.presenter.addToCart(feature).then(
                (res) => {
                    switch (res.status) {
                        case 200:
                            toast.success("Added to cart");
                            break;
                        case 406:
                            toast.warning("Point is already in an open notification");
                            break;
                        case 409:
                            toast.warning("Already in cart");
                            break;
                        default:
                            toast.error("Failed to add to cart");
                            break;
                    };
                    return res.json();
                }
            ).then((res) => {
                if (res.cartlength) props.onSetCartCounter(res.cartlength);
            });
        }} />
    </div>

};

function FeatureWindowActions(props: {
    onClearAll: () => void,
    onClose: () => void,
}) {
    return <>
        <Tooltip title="Clear open points and markers" placement="bottom" arrow disableInteractive>
            <div className='fi-close pt-1' onClick={() => { if (confirm("Close point information window and clear open points and markers?")) props.onClearAll() }}>
                <FontAwesomeIcon icon={faDeleteLeft} />
            </div>
        </Tooltip>
        {/* <Tooltip title="Minimise" placement="bottom" arrow disableInteractive>
            <div className='fi-close pt-1' onClick={props.onMinimise}>
                <FontAwesomeIcon icon={faMinus} />
            </div>
        </Tooltip> */}
        <Tooltip title="Close window" placement="bottom" arrow disableInteractive>
            <div className='fi-close' onClick={props.onClose}>
                <FontAwesomeIcon icon={faX} />
            </div>
        </Tooltip>
    </>;
}

function FeatureWindowModeItem(props: {
    id: number,
    fisd: FeatureInfoStoreData,
    index: number,
    presenter: MapPresenter,
    dimensions?: { startX?: number, startY?: number, width?: number, height?: number },
    onDimensionChange?: (dimensions: FloatingWindowDimensions) => void,
    setFeatureList: (featureList: FeatureInfoStore) => void,
    onSetCartCounter: (count: number) => void,
    onClearAllFeatures: () => void,
    closeRemovesPoint: boolean,
    isVisible: boolean,
    anonymousUser: boolean,
    context: OutletIProps,
    latestForecast: string,
    currentPresentation?: FeatureWindowPresentation,
    onPresentationChange?: (presentation: FeatureWindowPresentation) => void,
    onHide: () => void,
}) {
    const draggableWindow = useRef<HTMLDivElement>(null);
    const windowActions = <FeatureWindowActions
        onClearAll={props.onClearAllFeatures}
        onClose={() => {
            if (props.closeRemovesPoint) {
                props.setFeatureList(props.presenter.removeFeatureInfo(props.id));
            } else {
                props.onHide();
            }
        }}
    />;

    return <FloatingWindow
        ref={draggableWindow}
        id={`feature-window-${props.id}`}
        visible={props.isVisible}
        startX={props.dimensions?.startX && props.dimensions.startX + 5 * props.index}
        startY={props.dimensions?.startY && props.dimensions.startY + 15 * props.index}
        width={props.dimensions?.width}
        height={props.dimensions?.height}
        onDimensionChange={props.onDimensionChange}
        currentPresentation={props.currentPresentation}
        onPresentationChange={props.onPresentationChange}
        extraActions={windowActions}>
        <div className="accordion" id={`featureInfoAccordion-${props.id}`} style={{
            backgroundColor: "white",
            padding: "0.5rem 0.5rem 0 0.5rem",
        }}>
            <FeatureSingleItem
                key={`feature-accordion-${props.id}`}
                id={props.id}
                fisd={props.fisd}
                presenter={props.presenter}
                presentation={FeatureWindowPresentation.WINDOWS}
                collapsed={true}
                draggableWindow={draggableWindow}
                setFeatureList={props.setFeatureList}
                onSetCartCounter={props.onSetCartCounter}
                anonymousUser={props.anonymousUser}
                context={props.context}
                latestForecast={props.latestForecast}
            />
        </div>
    </FloatingWindow>;
}

/**
 * WINDOWS mode: each feature gets its own independent FloatingWindow with
 * individual visibility tracking. Closing one window does not affect others.
 */
export function FeatureWindowsMode(props: {
    featureList: FeatureInfoStore,
    globalVisible: boolean,
    presenter: MapPresenter,
    dimensions?: { startX?: number, startY?: number, width?: number, height?: number },
    onDimensionChange?: (dimensions: FloatingWindowDimensions) => void,
    setFeatureList: (featureList: FeatureInfoStore) => void,
    onSetCartCounter: (count: number) => void,
    onClearAllFeatures: () => void,
    onCloseAll: () => void,
    anonymousUser: boolean,
    context: OutletIProps,
    latestForecast: string,
    currentPresentation?: FeatureWindowPresentation,
    onPresentationChange?: (presentation: FeatureWindowPresentation) => void,
    closeRemovesPoint: boolean,
}) {
    const [hiddenWindows, setHiddenWindows] = useState<Set<number>>(new Set());

    // When new features are added, make sure they're visible
    useEffect(() => {
        const currentIds = new Set(Object.keys(props.featureList).map(Number));
        setHiddenWindows(prev => {
            // Remove hidden entries that no longer exist in featureList
            const next = new Set([...prev].filter(id => currentIds.has(id)));
            return next.size !== prev.size ? next : prev;
        });
    }, [props.featureList]);

    // If all windows are hidden, notify parent
    useEffect(() => {
        const featureIds = Object.keys(props.featureList).map(Number);
        if (featureIds.length > 0 && featureIds.every(id => hiddenWindows.has(id))) {
            props.onCloseAll();
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- props.onCloseAll is an inline callback from the parent; adding it would re-run this effect on every parent render and re-notify
    }, [hiddenWindows, props.featureList]);

    const hideWindow = (id: number) => {
        setHiddenWindows(prev => new Set([...prev, id]));
    };

    return <>{Object.entries(props.featureList).map(([idS, fisd], index) => {
        const id = parseInt(idS);
        const isVisible = props.globalVisible && !hiddenWindows.has(id);

        return <FeatureWindowModeItem
            key={`feature-window-${id}`}
            id={id}
            fisd={fisd}
            index={index}
            presenter={props.presenter}
            dimensions={props.dimensions}
            onDimensionChange={props.onDimensionChange}
            setFeatureList={props.setFeatureList}
            onSetCartCounter={props.onSetCartCounter}
            onClearAllFeatures={props.onClearAllFeatures}
            closeRemovesPoint={props.closeRemovesPoint}
            isVisible={isVisible}
            anonymousUser={props.anonymousUser}
            context={props.context}
            latestForecast={props.latestForecast}
            currentPresentation={props.currentPresentation}
            onPresentationChange={props.onPresentationChange}
            onHide={() => hideWindow(id)}
        />;
    })}</>;
}

export default memo(function FeatureInfo(props: {
    presenter: MapPresenter,
    feature: UseFeatureInfoReturn,
    onSetCartCounter: (count: number) => void,
    onClearAllFeatures: () => void,
    latestForecast: string,
    closeRemovesPoint: boolean
}) {
    const context = useOutletContext<OutletIProps>();
    const anonymousUser = context.user.loggedIn === LoginState.Failed;

    const [featureList, setFeatureList] = useState<FeatureInfoStore>(props.feature.data);
    const draggableWindow = useRef<HTMLDivElement>(null);
    const featureInfoAccordion = useRef<HTMLDivElement>(null);
    const [windowVisible, setWindowVisible] = useState<boolean>(false);

    useEffect(() => {
        const keys = Object.keys(props.feature.data);
        if (props.feature.presentation === FeatureWindowPresentation.ONE && keys.length > 1) {
            // only get the last feature
            const lastKey = keys.pop();
            if (lastKey)
                setFeatureList({ [lastKey]: props.feature.data[lastKey] });
        } else {
            setFeatureList(props.feature.data);
        }
    }, [props.feature, props.feature.presentation]);

    useEffect(() => {
        if (props.feature.windowVisible !== undefined) {
            setWindowVisible(props.feature.windowVisible);
        }
    }, [props.feature.windowVisible]);

    useEffect(() => {
        if (Object.keys(featureList).length === 0) {
            setWindowVisible(false);
            props.feature.setWindowVisible(false);
        } else {
            if (props.feature.presentation === FeatureWindowPresentation.LIST && featureInfoAccordion.current && featureInfoAccordion.current.hasChildNodes()) {
                const innerElem = featureInfoAccordion.current;
                for (const child of innerElem.children) {
                    child.children[0].children[0].children[0].classList.add('collapsed');
                    child.children[1].classList.remove('show');
                }

                innerElem.lastElementChild?.children[0].children[0].children[0].classList.remove('collapsed');
                innerElem.lastElementChild?.children[1].classList.add('show');
            }
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- props.feature is a per-render object; adding it would re-run this DOM-sync effect whenever the feature object identity changes
    }, [featureList]);

    const listExtraActions = <FeatureWindowActions
        onClearAll={props.onClearAllFeatures}
        onClose={() => {
            props.feature.setWindowVisible(false);
            setWindowVisible(false);
        }}
    />;

    const oneExtraActions = <FeatureWindowActions
        onClearAll={props.onClearAllFeatures}
        onClose={() => {
            if (props.closeRemovesPoint) {
                const id = Object.keys(featureList).map(Number).pop();
                if (id !== undefined) {
                    setFeatureList(props.presenter.removeFeatureInfo(id));
                }
                props.feature.setWindowVisible(false);
                setWindowVisible(false);
            } else {
                props.feature.setWindowVisible(false);
                setWindowVisible(false);
            }
        }}
    />;

    // TODO Refactor this to minimise duplicated code, and remove presenter.
    // Also rewrite the accordion to not use bootstrap, it will probably improve the performance for many open features
    // because then they can be hidden from the DOM when folded instead of just hidden with CSS
    const lastEntryId = Object.keys(featureList).length - 1;
    return Object.keys(featureList).length > 0 &&
        props.feature.presentation === FeatureWindowPresentation.LIST && (
            <FloatingWindow
                ref={draggableWindow}
                id={`feature-window-${lastEntryId}`}
                key={`feature-window-${lastEntryId}`}
                visible={windowVisible}
                startX={props.feature.dimensions?.startX}
                startY={props.feature.dimensions?.startY}
                width={props.feature.dimensions?.width}
                height={props.feature.dimensions?.height}
                onDimensionChange={props.feature.onDimensionChange}
                currentPresentation={props.feature.presentation}
                onPresentationChange={props.feature.onPresentationChange}
                extraActions={listExtraActions}>
                <div ref={featureInfoAccordion} className="accordion" id="featureInfoAccordion" style={{
                    backgroundColor: "white",
                    padding: "0.5rem 0.5rem 0 0.5rem",
                }}>
                    {Object.entries(featureList).map(([idS, fisd], index) => {
                        const id = parseInt(idS);
                        return <FeatureAccordionItem
                            key={`feature-accordion-${id}`}
                            id={id}
                            fisd={fisd}
                            presenter={props.presenter}
                            presentation={props.feature.presentation}
                            collapsed={index === lastEntryId}
                            draggableWindow={draggableWindow}
                            setFeatureList={setFeatureList}
                            onSetCartCounter={props.onSetCartCounter}
                            anonymousUser={anonymousUser}
                            context={context}
                            latestForecast={props.latestForecast}
                        />
                    })}
                </div>
            </FloatingWindow>
        ) ||
        props.feature.presentation === FeatureWindowPresentation.ONE && (
            <FloatingWindow
                id={`feature-window-1`}
                key={`feature-window-1`}
                ref={draggableWindow}
                visible={windowVisible}
                startX={props.feature.dimensions?.startX}
                startY={props.feature.dimensions?.startY}
                width={props.feature.dimensions?.width}
                height={props.feature.dimensions?.height}
                onDimensionChange={props.feature.onDimensionChange}
                currentPresentation={props.feature.presentation}
                onPresentationChange={props.feature.onPresentationChange}
                extraActions={oneExtraActions}>
                <div ref={featureInfoAccordion} className="accordion" id="featureInfoAccordion" style={{
                    backgroundColor: "white",
                    padding: "0.5rem 0.5rem 0 0.5rem",
                }}>
                    {Object.entries(featureList).map(([idS, fisd]) => {
                        const id = parseInt(idS);
                        return <FeatureSingleItem
                            key={`feature-single-${id}`}
                            id={id}
                            fisd={fisd}
                            presenter={props.presenter}
                            presentation={props.feature.presentation}
                            collapsed={true}
                            draggableWindow={draggableWindow}
                            setFeatureList={setFeatureList}
                            onSetCartCounter={props.onSetCartCounter}
                            anonymousUser={anonymousUser}
                            context={context}
                            latestForecast={props.latestForecast}
                        />
                    })}
                </div>
            </FloatingWindow>)
        ||
        <FeatureWindowsMode
            featureList={featureList}
            globalVisible={windowVisible}
            presenter={props.presenter}
            dimensions={props.feature.dimensions}
            onDimensionChange={props.feature.onDimensionChange}
            setFeatureList={setFeatureList}
            onSetCartCounter={props.onSetCartCounter}
            onClearAllFeatures={props.onClearAllFeatures}
            onCloseAll={() => {
                props.feature.setWindowVisible(false);
                setWindowVisible(false);
            }}
            anonymousUser={anonymousUser}
            context={context}
            latestForecast={props.latestForecast}
            currentPresentation={props.feature.presentation}
            onPresentationChange={props.feature.onPresentationChange}
            closeRemovesPoint={props.closeRemovesPoint}
        />
});